import React from "react";

import Select, { components } from "react-select";

import { ReactComponent as DropdownArrowDown } from "../../../../assets/icons/dropdownArrowDown.svg";
import { ReactComponent as DropdownArrowUp } from "../../../../assets/icons/dropdownArrowUp.svg";

import moment from "moment";
import SimpleDatePicker from "@/components/common/SimpleDatePicker";
import useRoles from "../../../../hooks/useRoles";
import {
  capitalizeFirstLetter,
  getTotalCapsulesLabel,
  pluralizeUnit,
  prescriberName,
} from "../../../../shared/helpers";
import ToolTipWeeksPerCycle from "./ToolTipWeeksPerCycle";

const DropdownIndicator = (props) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ? (
          <DropdownArrowUp />
        ) : (
          <DropdownArrowDown />
        )}
      </components.DropdownIndicator>
    )
  );
};

const SELECT_PLACEHOLDER = "Select";
const DATE_DISPLAY_FORMAT = "DD / MM / YYYY";
const FULL_WIDTH_STYLE = { width: "100%" };
const formatDate = (date) =>
  date ? moment(date).format(DATE_DISPLAY_FORMAT) : "";

/** Light grey panel behind the open react-select option list */
const DIGITAL_PAF_SELECT_STYLES = {
  menu: (base) => ({
    ...base,
    backgroundColor: "#f3f4f6",
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: "#f3f4f6",
    padding: "4px 0",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#e5e7eb"
      : state.isFocused
        ? "#eceff1"
        : "transparent",
    cursor: "pointer",
  }),
};

const tw = {
  section: "mt-3 mb-3 min-h-125",
  grid2: "grid gap-6 md:grid-cols-2",
  field: "relative grid mb-1",
  label: "text-[#00567a]",
  labelMuted: "text-[#8c8c8c]",
  input:
    "mt-1 w-full border-0 border-b border-[#c8c8c8] text-lg leading-5.5 text-[#646464] outline-none",
  inputDisabled: "text-[#8c8c8c]",
  inputError: "border-b-[#e41818]",
  subsection: "space-y-4 mt-8",
  checkboxLabel: "flex items-start gap-3 text-base text-[#646464]",
  checkboxLabelBold: "flex items-start gap-3 text-lg font-bold text-[#646464]",
  checkbox:
    "mt-0.5 h-5 w-5 shrink-0 rounded border border-[#00567a] accent-[#00567a]",
  requiredText: "text-sm italic text-[#e41818]",
};

const DigitalPAF = (props) => {
  const {
    handleChange,
    registerPatient,
    handleChangeCheckBox,
    validateFields,
    updateRegisterPatient,
    showErrors,
    handleChangeSelect,
    isRenewal,
    deletePropertyValue,
    handleCapsules,
    prescriptionField,
    dropdownValue,
    checkedItems,
    checkedItemsPrescriber,
    handleCheckboxChange,
    prescriptionConfirmationRisks,
    prescriberConfirmations,
  } = props;

  const isMale = registerPatient.type === "male";
  const isWCBP = registerPatient.type === "womanOfCP";

  const pafTypeLabel = isMale ? "Male" : isWCBP ? "WCBP" : "WNCBP";
  const changeSource = isRenewal === true ? dropdownValue : prescriptionField;

  const { storageData } = useRoles();

  const deleteProperty = (propName) => {
    deletePropertyValue(propName);
  };

  const isInvalidField = (fieldName) =>
    showErrors && !isRenewal && !validateFields?.[fieldName]?.isValid;

  const renamedMolecule =
    isRenewal === true &&
    changeSource?.product?.map((option) => ({
      value: option.id,
      label: option.value,
      brocures: option.brochure,
    }));
  const filteredOptions =
    Array?.isArray(renamedMolecule) &&
    renamedMolecule?.filter((option) => {
      return option?.value !== registerPatient?.molecule?.value;
    });

  const renamedCapsuleCycle =
    !!changeSource &&
    changeSource?.capsule_cycle?.map((option) => ({
      value: option.id === -1 ? null : option.id,
      label:
        option.id === -1
          ? "None"
          : option.id === 0
            ? "Other (please state)"
            : option.value,
    }));

  const scheduleDisplayType = changeSource?.schedule_display_type;

  const noneOption = { id: null, label: "None" };
  const getOptionsWithNone = (options) => {
    return [noneOption, ...(options || [])];
  };

  const renamedOptions = (() => {
    switch (scheduleDisplayType) {
      case "weeks":
        return {
          weeksOptions: getOptionsWithNone(
            changeSource?.n_weeks?.map((option) => ({
              id: option.id,
              label: option.value,
            }))
          ),
          cyclesOptions: [],
        };
      case "weeks_per_cycle":
        return {
          weeksOptions: getOptionsWithNone(
            changeSource?.weeks_per_cycle?.map((option) => ({
              id: option.id,
              label: option.value,
            }))
          ),
          cyclesOptions: getOptionsWithNone(
            changeSource?.n_cycles?.map((option) => ({
              id: option.id,
              label: option.value,
            }))
          ),
        };
      default:
        return {
          weeksOptions: [],
          cyclesOptions: getOptionsWithNone(
            changeSource?.n_cycles?.map((option) => ({
              id: option.id,
              label: option.value,
            }))
          ),
        };
    }
  })();

  const renamedCapsules = registerPatient?.capsules?.map((option) => option);

  const myRename = renamedCapsules?.map((element) => ({
    label:
      element.label === null
        ? "None"
        : element.label === 0
          ? `Other ${pluralizeUnit(registerPatient?.unit_label ?? "capsule")}`
          : element.label + " " + pluralizeUnit(registerPatient?.unit_label ?? "capsule"),
    id: element.id,
    other: !!element.other && element.other,
  }));

  const renamedIndication = changeSource?.indication?.map((option) => ({
    is_clinical_trial: option.is_clinical_trial,
    is_indication_other: option.is_indication_other,
    id: option.id,
    label: option.id === -1 ? "None" : option.value,
  }));

  const patientCategoryLabel = isMale
    ? "Male"
    : isWCBP
      ? "Woman of Childbearing Potential"
      : "Woman of Non-Childbearing Potential";
  const instructions = [
    "Enter the full hospital name and address where the patient is treated",
    "Enter the indication and drug name",
    `Select the appropriate strength and quantity of individual ${pluralizeUnit(registerPatient?.product_unit_label ?? "capsule")} required for this prescription. The maximum quantity allowable for this patient is a ${isWCBP ? "4" : "12"} week supply`,
    ...(isWCBP ? ["Enter the most recent negative pregnancy test date."] : []),
  ];

  const populatedRename = Array.from(
    { length: changeSource?.strengths?.length },
    () => SELECT_PLACEHOLDER
  );
  !!myRename &&
    myRename?.forEach((item) => {
      const index = changeSource?.strengths?.findIndex(
        (element) => element.id === item.id || element.id === item.index
      );
      if (index !== -1) {
        populatedRename[index] = item;
      }
    });

  return (
    <div className="mt-4 mb-8" data-testid="patient-initiation-digital-paf">
      <h1
        className="text-lg font-semibold text-[#00567a] my-4"
        data-testid="patient-initiation-digital-paf-title"
      >
        {`Prescription Authorisation Form - ${!isRenewal ? pafTypeLabel : `Renewal  ${pafTypeLabel} `
          }`}
      </h1>

      <hr
        className="border-t border-[#c8c8c8]"
        data-testid="digital-paf-divider"
      />

      <div className={tw.section} data-testid="digital-paf-content">
        <ol className="pl-6" data-testid="digital-paf-instructions">
          {instructions.map((instruction, index) => (
            <li
              key={instruction}
              className={`${index === 0 ? "" : "pt-3 "}text-base text-neutral-800`}
              data-testid={`digital-paf-instruction-${index + 1}`}
            >
              {`${index + 1}. ${instruction}`}
            </li>
          ))}
        </ol>

        <div className={tw.subsection}>
          <h2 className="text-lg font-semibold text-[#00567a]" data-testid="digital-paf-patient-details-label">Patient Details</h2>
          <div className={tw.grid2}>
            <div className={tw.field}>
              <label
                className={tw.labelMuted}
                data-testid="digital-paf-patient-category-label"
              >
                Patient Category
              </label>
              <input
                type="text"
                name="patient_category"
                value={patientCategoryLabel}
                className={`${tw.input} ${tw.inputDisabled}`}
                disabled
                data-testid="digital-paf-patient-category-input"
              />
            </div>

            <div className={tw.field}>
              <label
                className={isInvalidField("patient_dob") ? "text-[#e41818]" : tw.label}
                data-testid="digital-paf-patient-dob-label"
              >
                {!isRenewal
                  ? "Patient Date of Birth:*"
                  : "Patient Date of Birth:"}
              </label>
              {
                !isRenewal ? (
                  <>
                    <SimpleDatePicker
                      id="patient_dob"
                      name="patient_dob"
                      value={registerPatient.patient_dob}
                      onChange={(date) =>
                        updateRegisterPatient("patient_dob", date ?? undefined)
                      }
                      allowFuture={false}
                      className="mt-1"
                      inputClassName={
                        isInvalidField("patient_dob") ? "border-red-500" : undefined
                      }
                      data-testid="digital-paf-patient-dob-input"
                    />
                    {showErrors && !validateFields?.patient_dob?.isValid && (
                      <span
                        className={tw.requiredText}
                        data-testid="digital-paf-patient-dob-required"
                      >
                        Required
                      </span>
                    )}
                  </>
                ) : (
                  <div style={FULL_WIDTH_STYLE}>
                    <input
                      style={FULL_WIDTH_STYLE}
                      type="text"
                      className={`${tw.input} ${tw.inputDisabled}`}
                      value={
                        formatDate(
                          registerPatient.patient_dob || registerPatient.dob
                        )
                      }
                      readOnly
                      data-testid="digital-paf-patient-dob-input"
                    />
                  </div>
                )
              }
            </div >
          </div >
          <div className={tw.grid2}>
            <div className={tw.field}>
              <label
                className={isInvalidField("patient_initials") ? "text-[#e41818]" : tw.label}
                data-testid="digital-paf-patient-initials-label"
              >
                {!isRenewal ? "Patient Initials:*" : "Patient Initials:"}
              </label>
              <input
                type="text"
                onChange={handleChange}
                name="patient_initials"
                value={registerPatient.patient_initials || ""}
                placeholder="Initials"
                className={`${tw.input} ${isInvalidField("patient_initials") ? tw.inputError : ""
                  } ${isRenewal ? tw.inputDisabled : ""}`}
                maxLength="3"
                readOnly={isRenewal}
                data-testid="digital-paf-patient-initials-input"
              />

              {showErrors &&
                !isRenewal &&
                !validateFields?.patient_initials?.isValid && (
                  <span
                    className={tw.requiredText}
                    data-testid="digital-paf-patient-initials-required"
                  >
                    Required
                  </span>
                )}
            </div>

            {!isWCBP ? (
              <div />
            ) : (
              <div className={tw.field}>
                <label
                  className={tw.label}
                  data-testid="digital-paf-pregnancy-test-label"
                >
                  Date of Last Negative Pregnancy Test:*
                </label>
                <SimpleDatePicker
                  id="pregnancy_test"
                  name="pregnancy_test"
                  value={registerPatient.pregnancy_test}
                  onChange={(date) => {
                    if (date == null) {
                      deleteProperty("pregnancy_test");
                    } else {
                      updateRegisterPatient("pregnancy_test", date);
                    }
                  }}
                  allowFuture={false}
                  className="mt-1"
                  data-testid="digital-paf-pregnancy-test-input"
                />
              </div>
            )}
          </div >
        </div >

        <div className={tw.subsection}>
          <h2
            className="text-lg font-semibold text-[#00567a]"
            data-testid="digital-paf-prescription-details-label"
          >
            Prescription Details
          </h2>
          <div className={tw.grid2}>
            <div className={tw.field}>
              <label
                className={
                  showErrors &&
                    !isRenewal &&
                    !validateFields?.hospital_name_and_address?.isValid
                    ? "text-[#e41818]"
                    : tw.label
                }
                data-testid="digital-paf-hospital-label"
              >
                Name and Address of treating Hospital:
              </label>
              {!isRenewal ? (
                <input
                  type="text"
                  name="hospital_name_and_address"
                  value={
                    storageData?.hospital?.value +
                    ", " +
                    storageData?.hospital?.address
                  }
                  placeholder="Hospital Name and Address"
                  className={`${tw.input} ${tw.inputDisabled}`}
                  disabled
                  data-testid="digital-paf-hospital-input"
                />
              ) : (
                <input
                  type="text"
                  name="hospital_name_and_address"
                  value={
                    registerPatient?.prescriber?.hospital?.name +
                    " " +
                    registerPatient?.prescriber?.hospital?.address?.full_address
                  }
                  placeholder="Hospital Name and Address"
                  className={`${tw.input} ${tw.inputDisabled}`}
                  disabled
                  data-testid="digital-paf-hospital-input"
                />
              )}
            </div>
            <div />
          </div>

          <div className="grid gap-6 md:grid-cols-2 mb-3">
            <div className={tw.field}>
              <label className={tw.label} data-testid="digital-paf-indication-label">
                Indication:*
              </label>
              {!isRenewal ? (
                <div data-testid="digital-paf-indication-select">
                  <Select
                    classNamePrefix="custom-select"
                    styles={DIGITAL_PAF_SELECT_STYLES}
                    components={{
                      DropdownIndicator: DropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    value={registerPatient.indication || ""}
                    onChange={(selectedOption) => {
                      handleCapsules(selectedOption, "indication");
                    }}
                    options={renamedIndication}
                    placeholder="Select"
                    className={
                      showErrors && !validateFields?.indication?.isValid
                        ? "error-border"
                        : ""
                    }
                  />
                </div>
              ) : (
                <div data-testid="digital-paf-indication-select">
                  <Select
                    classNamePrefix="custom-select"
                    styles={DIGITAL_PAF_SELECT_STYLES}
                    components={{
                      DropdownIndicator: DropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    value={registerPatient.indication || ""}
                    onChange={(selectedOption) => {
                      handleChangeSelect(selectedOption, "indication");
                    }}
                    options={renamedIndication}
                    placeholder="Select"
                  />
                </div>
              )}
            </div>
            <div className={tw.field}>
              <label className={tw.label} data-testid="digital-paf-molecule-label">
                Molecule:
              </label>
              {!isRenewal ? (
                <input
                  type="text"
                  value={registerPatient.drug || ""}
                  className={`${tw.input} ${tw.inputDisabled}`}
                  disabled
                  data-testid="digital-paf-molecule-input"
                />
              ) : (
                <div data-testid="digital-paf-molecule-select">
                  <Select
                    classNamePrefix="custom-select"
                    styles={DIGITAL_PAF_SELECT_STYLES}
                    components={{
                      DropdownIndicator: DropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    value={registerPatient.molecule || ""}
                    onChange={(selectedOption) => {
                      handleChangeSelect(selectedOption, "Molecule");
                    }}
                    options={filteredOptions}
                    placeholder="Select"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="space-y-3">
            {(registerPatient.indication?.is_indication_other === true ||
              registerPatient?.indication?.label.includes(
                "Other (Please State)"
              )) && (
                <div className="grid gap-6 md:grid-cols-2 mb-3">
                  <div className={tw.field}>
                    <label
                      className={tw.label}
                      data-testid="digital-paf-indication-other-label"
                    >
                      Other indication:*
                    </label>

                    <input
                      type="text"
                      onChange={(e) => {
                        // Remove emojis using Unicode property escapes
                        const cleanedValue = e.target.value.replace(/\p{Emoji_Presentation}|\p{Extended_Pictographic}/gu, "");
                        if (isRenewal === true) {
                          handleChangeSelect(cleanedValue, "indication_other");
                        } else {
                          handleCapsules(cleanedValue, "indication_other");
                        }
                      }}
                      name="indication_other"
                      value={registerPatient?.indication_other || ""}
                      placeholder="Other indication"
                      maxLength={75}
                      className={tw.input}
                      data-testid="digital-paf-indication-other-input"
                    />
                    {showErrors && !validateFields?.indication_other?.isValid && (
                      <span
                        className={tw.requiredText}
                        data-testid="digital-paf-indication-other-required"
                      >
                        Required
                      </span>
                    )}
                  </div>
                  <div />
                </div>
              )}
            {(registerPatient.indication?.is_clinical_trial === true ||
              registerPatient?.indication?.label.includes(
                "Clinical trial"
              )) && (
                <div className="grid gap-6 md:grid-cols-2 mb-3">
                  <div className={tw.field}>
                    <label
                      className={tw.label}
                      data-testid="digital-paf-clinical-trial-label"
                    >
                      Clinical Trial
                    </label>

                    <input
                      type="text"
                      onChange={(e) => {
                        isRenewal === true
                          ? handleChangeSelect(e.target.value, "clinical_trial")
                          : handleCapsules(e.target.value, "clinical_trial");
                      }}
                      name="clinical_trial"
                      value={registerPatient?.clinical_trial || ""}
                      placeholder="Clinical indication"
                      maxLength={75}
                      className={tw.input}
                      data-testid="digital-paf-clinical-trial-input"
                    />
                    {showErrors && !validateFields?.clinical_trial?.isValid && (
                      <span
                        className={tw.requiredText}
                        data-testid="digital-paf-clinical-trial-required"
                      >
                        Required
                      </span>
                    )}
                  </div>
                  <div />
                </div>
              )}
          </div>

          <div className="grid gap-6 md:grid-cols-3 mb-3">
            <div className={tw.field}>
              <label
                className={tw.label}
                data-testid="digital-paf-dosage-strength-label"
              >
                {scheduleDisplayType === "weeks_per_cycle"
                  ? "Dosage Strength*"
                  : `${capitalizeFirstLetter(registerPatient?.unit_label ?? `${capitalizeFirstLetter(registerPatient?.unit_label ?? "capsule")}`)} Strength*`}
              </label>
              {changeSource?.strengths?.map((element, key) => (
                <div
                  key={element.id}
                  style={{ marginTop: key === 0 ? "15px" : "0" }}
                >
                  <input
                    type="text"
                    onChange={handleChange}
                    value={element.value}
                    className={`${tw.input} mb-1.25 ${tw.inputDisabled}`}
                    disabled
                    data-testid={`digital-paf-dosage-strength-input-${element.id}`}
                  />
                  {populatedRename[key]?.label === `Other ${pluralizeUnit(registerPatient?.unit_label ?? "capsule")}` && (
                    <div
                      className="mb-1.25"
                      style={{ paddingBottom: "80px" }}
                    ></div>
                  )}
                </div>
              ))}
            </div>
            <div className={`${tw.field} whitespace-nowrap`}>
              <label
                className={tw.label}
                data-testid="digital-paf-capsules-per-cycle-label"
              >
                {scheduleDisplayType === "weeks"
                  ? `Number of ${pluralizeUnit(registerPatient?.unit_label ?? "capsule")} per 4 week supply:*`
                  : `${capitalizeFirstLetter(pluralizeUnit(registerPatient?.unit_label ?? "capsule"))} per Cycle:*`}
              </label>
              <i className="min-h-6.5 whitespace-nowrap italic text-[#646464]">(Not pack size)</i>

              {changeSource?.strengths?.map((element, key) => (
                <div key={key}>
                  <div data-testid={`digital-paf-capsules-per-cycle-select-${element.id}`}>
                    <Select
                      placeholder="Select"
                      classNamePrefix="margin-custom-select custom-select"
                      styles={DIGITAL_PAF_SELECT_STYLES}
                      components={{
                        DropdownIndicator: DropdownIndicator,
                        IndicatorSeparator: () => null,
                      }}
                      value={
                        populatedRename !== undefined ? populatedRename[key] : ""
                      }
                      onChange={(selectedOption) => {
                        isRenewal === true
                          ? handleChangeSelect(
                            selectedOption,
                            "capsule_per_cycle",
                            element,
                            element.id
                          )
                          : handleCapsules(
                            selectedOption,
                            "capsule_per_cycle",
                            element,
                            element.id
                          );
                      }}
                      options={renamedCapsuleCycle}
                    />
                  </div>

                  {!!populatedRename &&
                    populatedRename[key]?.label === `Other ${pluralizeUnit(registerPatient?.unit_label ?? "capsule")}` && (
                      <div className="row mb-3">
                        <div className="col">
                          <label data-testid={`digital-paf-capsules-other-label-${element.id}`}>
                            {scheduleDisplayType
                              ? "Quantity:*"
                              : `Other ${pluralizeUnit(registerPatient?.unit_label ?? "capsule")} per Cycle:*`}
                          </label>
                          <input
                            type="number"
                            onChange={(e) => {
                              isRenewal === true
                                ? handleChangeSelect(
                                  e.target.value,
                                  "capsule_per_cycle",
                                  element
                                )
                                : handleCapsules(
                                  e.target.value,
                                  "capsule_per_cycle",
                                  element
                                );
                            }}
                            value={
                              populatedRename[key].other !== undefined &&
                                populatedRename[key].other !== false
                                ? populatedRename[key].other
                                : 1
                            }
                            name={`capsule_cycle_other_${element.id}`}
                            placeholder={`Other ${pluralizeUnit(registerPatient?.unit_label ?? "capsule")} per Cycle`}
                            min={1}
                            className={tw.input}
                            data-testid={`digital-paf-capsules-other-input-${element.id}`}
                          />
                        </div>
                      </div>
                    )}
                </div>
              ))}
            </div>

            {scheduleDisplayType === "weeks_per_cycle" ? (
              <div >
                <label
                  className="flex"
                  data-testid="digital-paf-weeks-per-cycle-label"
                >
                  {"Supply weeks per cycle:*"}
                  <ToolTipWeeksPerCycle data-testid="digital-paf-weeks-per-cycle-tooltip" />
                </label>
                <div data-testid="digital-paf-weeks-per-cycle-select">
                  <Select
                    classNamePrefix="margin-custom-select custom-select"
                    styles={DIGITAL_PAF_SELECT_STYLES}
                    components={{
                      DropdownIndicator: DropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    value={
                      !!registerPatient.weeks_per_cycle
                        ? registerPatient.weeks_per_cycle
                        : { id: null, label: "Select" }
                    }
                    onChange={(selectedOption) => {
                      isRenewal === true
                        ? handleChangeSelect(selectedOption, "weeks_per_cycle")
                        : handleCapsules(selectedOption, "weeks_per_cycle");
                    }}
                    options={renamedOptions.weeksOptions}
                    placeholder="Select"
                  />
                </div>

                {registerPatient?.weeks_per_cycle &&
                  registerPatient?.weeks_per_cycle?.label ===
                  "Other (please state)" && (
                    <div className="grid gap-6 md:grid-cols-2 mb-3">
                      <div className={tw.field}>
                        <label
                          className={tw.label}
                          data-testid="digital-paf-weeks-per-cycle-other-label"
                        >
                          Other Weeks:*
                        </label>
                        <input
                          type="number"
                          name={`capsule_cycle_other_$`}
                          value={
                            registerPatient.other
                              ? registerPatient.other
                              : 1 || ""
                          }
                          onChange={(e) => {
                            isRenewal === true
                              ? handleChangeSelect(
                                e.target.value,
                                "number_weeks_other"
                              )
                              : handleCapsules(
                                e.target.value,
                                "number_weeks_other"
                              );
                          }}
                          placeholder="Other Weeks"
                          min={1}
                          className={tw.input}
                          data-testid="digital-paf-weeks-per-cycle-other-input"
                        />
                      </div>
                    </div>
                  )}
              </div>
            ) : (
              <div className={tw.field}>
                <label
                  style={{ paddingBottom: "25px" }}
                  data-testid="digital-paf-cycles-or-weeks-label"
                >
                  {scheduleDisplayType === "weeks"
                    ? "Number of supply weeks:*"
                    : " Number of Cycles:*"}
                </label>
                <div data-testid="digital-paf-cycles-or-weeks-select">
                  <Select
                    classNamePrefix="margin-custom-select custom-select"
                    styles={DIGITAL_PAF_SELECT_STYLES}
                    components={{
                      DropdownIndicator: DropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    value={
                      scheduleDisplayType && !!registerPatient.number_weeks
                        ? registerPatient.number_weeks
                        : !!registerPatient.number_cycles
                          ? registerPatient.number_cycles
                          : { id: null, label: "Select" }
                    }
                    onChange={(selectedOption) => {
                      isRenewal === true
                        ? handleChangeSelect(
                          selectedOption,
                          scheduleDisplayType === "cycles"
                            ? "number_cycles"
                            : "number_weeks"
                        )
                        : handleCapsules(
                          selectedOption,
                          scheduleDisplayType === "cycles"
                            ? "number_cycles"
                            : "number_weeks"
                        );
                    }}
                    options={
                      scheduleDisplayType === "cycles"
                        ? renamedOptions.cyclesOptions
                        : renamedOptions.weeksOptions
                    }
                    placeholder="Select"
                  />
                </div>

                {registerPatient.number_weeks &&
                  registerPatient.number_weeks.label ===
                  "Other (Please State)" && (
                    <div className="grid gap-6 md:grid-cols-2 mb-3">
                      <div className={tw.field}>
                        <label
                          className={tw.label}
                          data-testid="digital-paf-number-weeks-other-label"
                        >
                          Other Weeks:*
                        </label>
                        <input
                          type="number"
                          name={`capsule_cycle_other_$`}
                          value={
                            registerPatient.other
                              ? registerPatient.other
                              : 1 || ""
                          }
                          onChange={(e) => {
                            isRenewal === true
                              ? handleChangeSelect(
                                e.target.value,
                                "number_weeks_other"
                              )
                              : handleCapsules(
                                e.target.value,
                                "number_weeks_other"
                              );
                          }}
                          placeholder="Other Weeks"
                          min={1}
                          className={tw.input}
                          data-testid="digital-paf-number-weeks-other-input"
                        />
                      </div>
                    </div>
                  )}
              </div>
            )}

            <div className={tw.field}>
              {scheduleDisplayType === "weeks_per_cycle" && (
                <>
                  <label
                    style={{ paddingBottom: "25px" }}
                    data-testid="digital-paf-number-cycles-label"
                  >
                    {scheduleDisplayType === "weeks"
                      ? "Number of supply weeks:*"
                      : " Number of Cycles:*"}
                  </label>
                  <div data-testid="digital-paf-number-cycles-select">
                    <Select
                      classNamePrefix="margin-custom-select custom-select"
                      styles={DIGITAL_PAF_SELECT_STYLES}
                      components={{
                        DropdownIndicator: DropdownIndicator,
                        IndicatorSeparator: () => null,
                      }}
                      value={
                        scheduleDisplayType && !!registerPatient.number_weeks
                          ? registerPatient.number_weeks
                          : !!registerPatient.number_cycles
                            ? registerPatient.number_cycles
                            : { id: null, label: "Select" }
                      }
                      onChange={(selectedOption) => {
                        isRenewal === true
                          ? handleChangeSelect(selectedOption, "number_cycles")
                          : handleCapsules(selectedOption, "number_cycles");
                      }}
                      options={renamedOptions.cyclesOptions}
                      placeholder="Select"
                    />
                  </div>
                </>
              )}

              <div
                className={`grid ${scheduleDisplayType === "weeks_per_cycle" && "mt-3"
                  }`}
              >
                <label
                  className="flex"
                  data-testid="digital-paf-total-supply-label"
                >
                  {getTotalCapsulesLabel(scheduleDisplayType, registerPatient?.unit_label || 'capsule')}{" "}
                  {scheduleDisplayType === "weeks_per_cycle" && (
                    <ToolTipWeeksPerCycle
                      TotalSupply
                      data-testid="digital-paf-total-supply-tooltip"
                    />
                  )}
                </label>
                <input
                  type="text"
                  onChange={handleChange}
                  name="total_capsules"
                  value={
                    scheduleDisplayType === "weeks_per_cycle"
                      ? !!registerPatient.total_weeks_supply
                        ? registerPatient.total_weeks_supply + " weeks"
                        : ""
                      : registerPatient.total_capsules || ""
                  }
                  className={`${tw.input} mb-1.25 ${tw.inputDisabled}`}
                  placeholder="No Total"
                  disabled
                  data-testid="digital-paf-total-supply-input"
                  style={{
                    paddingTop:
                      scheduleDisplayType === "weeks_per_cycle"
                        ? "5px"
                        : "15px",
                  }}
                />
              </div>
            </div>
          </div>

          {isRenewal &&
            !!prescriptionConfirmationRisks &&
            prescriptionConfirmationRisks?.map((item) => (
              <div key={item.id} className="mt-2 mb-2">
                <label
                  className={tw.checkboxLabel}
                  htmlFor={`confirmation-${item.id}`}
                  data-testid={`digital-paf-confirmation-risk-label-${item.id}`}
                >
                  <input
                    type="checkbox"
                    id={`confirmation-${item.id}`}
                    name={`confirmation-${item.id}`}
                    className={tw.checkbox}
                    checked={item.confirmation_risk_patient}
                    onChange={(e) => handleChangeCheckBox(e, item.id)}
                    data-testid={`digital-paf-confirmation-risk-checkbox-${item.id}`}
                  />
                  <span>{item.confirmation_text}</span>
                </label>
              </div>
            ))}

          {
            <div>
              {prescriptionField?.prescription_confirmation_risks?.map(
                (item) => (
                  <div key={item.id} className="mt-2 mb-2">
                    <label
                      className={`${tw.checkboxLabel} ${registerPatient?.confirmation_checked_by_pharmacist ===
                        true
                        ? "text-[#00a03b]"
                        : ""
                        }`}
                      htmlFor={`risk-checkbox-${item.id}`}
                      data-testid={`digital-paf-risk-checkbox-label-${item.id}`}
                    >
                      <input
                        type="checkbox"
                        id={`risk-checkbox-${item.id}`}
                        name={`risk-checkbox-${item.id}`}
                        className={tw.checkbox}
                        checked={
                          checkedItems[`risk-checkbox-${item.id}`] || false
                        }
                        onChange={handleCheckboxChange}
                        data-testid={`digital-paf-risk-checkbox-${item.id}`}
                      />
                      <span>{item.confirmation_text}</span>
                    </label>
                  </div>
                )
              )}
            </div>
          }
        </div>

        <h2
          className="mt-5 text-lg font-semibold text-[#8c8c8c]"
          data-testid="digital-paf-prescriber-declaration-label"
        >
          Prescriber Declaration:
        </h2>

        <div className="mt-6 mb-10 space-y-4">
          <div className={tw.grid2}>
            <div className={tw.field}>
              <label
                className={tw.labelMuted}
                data-testid="digital-paf-prescriber-name-label"
              >
                Prescriber Name
              </label>
              <input
                type="text"
                className={`${tw.input} ${tw.inputDisabled}`}
                name="prescriberName"
                value={prescriberName() || ""}
                disabled
                data-testid="digital-paf-prescriber-name-input"
              />
            </div>
            <div className={tw.field}>
              <label
                className={tw.labelMuted}
                data-testid="digital-paf-date-of-paf-label"
              >
                Date of PAF
              </label>
              <input
                className={`${tw.input} ${tw.inputDisabled}`}
                value={moment().format("DD / MM / YYYY")}
                disabled
                data-testid="digital-paf-date-of-paf-input"
              />
            </div>
          </div>
        </div>
        <div>
          {
            <div>
              {prescriptionField?.prescriber_confirmations?.map((item) => (
                <div key={item.id} className="mt-2 mb-2">
                  <label
                    className={
                      `${tw.checkboxLabelBold} ` +
                      (showErrors &&
                        !validateFields?.confirmation_risk_prescriber?.isValid &&
                        !checkedItemsPrescriber[`checkbox-prescriber-${item.id}`]
                        ? "text-[#e41818]"
                        : "")
                    }
                    htmlFor={`checkbox-prescriber-${item.id}`}
                    data-testid={`digital-paf-prescriber-confirmation-label-${item.id}`}
                  >
                    <input
                      type="checkbox"
                      id={`checkbox-prescriber-${item.id}`}
                      name={`checkbox-prescriber-${item.id}`}
                      className={tw.checkbox}
                      checked={
                        checkedItemsPrescriber[
                        `checkbox-prescriber-${item.id}`
                        ] || false
                      }
                      onChange={handleCheckboxChange}
                      data-testid={`digital-paf-prescriber-confirmation-checkbox-${item.id}`}
                    />
                    <span>{item.confirmation_text}</span>
                  </label>
                </div>
              ))}
            </div>
          }

          {/* Renewal */}
          {isRenewal &&
            !!prescriberConfirmations &&
            prescriberConfirmations?.map((item, index) => (
              <div key={item.id} className="mt-2 mb-2">
                <label
                  className={
                    `${tw.checkboxLabelBold} ` +
                    (showErrors &&
                      !validateFields?.confirmation_risk_prescriber?.isValid &&
                      !item.prescriber_confirmations
                      ? "text-[#e41818]"
                      : "")
                  }
                  htmlFor={`checkbox-prescriber-${item.id}`}
                  data-testid={`digital-paf-renewal-prescriber-confirmation-label-${item.id}`}
                >
                  <input
                    type="checkbox"
                    id={`checkbox-prescriber-${item.id}`}
                    name={`checkbox-prescriber-${item.id}`}
                    className={tw.checkbox}
                    checked={item.prescriber_confirmations}
                    onChange={(e) => handleChangeCheckBox(e, item.id)}
                    data-testid={`digital-paf-renewal-prescriber-confirmation-checkbox-${item.id}`}
                  />
                  <span>{item.confirmation_text}</span>
                </label>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
export default DigitalPAF;
